import type React from "react";
import { lazy, memo, Suspense, useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import "./MarkdownRenderer.css";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { getApiBase } from "./api";

const Mermaid = lazy(() =>
	import("./Mermaid").then((m) => ({ default: m.Mermaid })),
);

interface MarkdownRendererProps {
	content: string;
	className?: string;
}

interface ParsedAlt {
	altText: string;
	width?: string;
	height?: string;
	alignment?: "left" | "right" | "center";
}

const parseToken = (part: string) => {
	if (part === "left" || part === "right" || part === "center") {
		return { alignment: part as "left" | "right" | "center" };
	}
	if (/^\d+$/.test(part)) {
		return { width: part };
	}
	if (/^\d+x\d+$/.test(part)) {
		const dims = part.split("x");
		return { width: dims[0], height: dims[1] };
	}
	return {};
};

const parseAltText = (alt: string): ParsedAlt => {
	let altText = alt || "";
	let width: string | undefined;
	let height: string | undefined;
	let alignment: "left" | "right" | "center" | undefined;

	if (altText.includes("|")) {
		const parts = altText.split("|");
		altText = (parts[0] || "").trim();
		for (let i = 1; i < parts.length; i++) {
			const token = parseToken((parts[i] || "").trim().toLowerCase());
			if (token.alignment) alignment = token.alignment;
			if (token.width) width = token.width;
			if (token.height) height = token.height;
		}
	}

	return { altText, width, height, alignment };
};

const MarkdownImage: React.FC<{
	src?: string;
	alt?: string;
	title?: string;
	onZoom: (src: string) => void;
}> = ({ src, alt, title, onZoom }) => {
	const [hasError, setHasError] = useState(false);
	const resolvedSrc = src?.startsWith("/api/") ? `${getApiBase()}${src}` : src;

	if (hasError || !resolvedSrc) {
		return (
			<div className="image-error-fallback">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					className="image-error-icon"
					aria-hidden="true"
				>
					<rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
					<line x1="2" x2="22" y1="2" y2="22" />
					<path d="M10.41 10.41 12 12M17.42 12.42 19 14" />
				</svg>
				<span className="image-error-text">
					Image failed to load: {alt || resolvedSrc || "unnamed file"}
				</span>
			</div>
		);
	}

	const { altText, width, height, alignment } = parseAltText(alt || "");

	// Sizing styles
	const imgStyle: React.CSSProperties = {};
	if (width) imgStyle.width = `${width}px`;
	if (height) imgStyle.height = `${height}px`;

	// Align class
	const alignClass = alignment ? ` align-${alignment}` : "";

	// Caption content (prefers title, fallbacks to altText if not empty and was modified)
	const captionText = title || (altText && alt !== altText ? altText : "");

	const handleImageClick = () => {
		onZoom(resolvedSrc);
	};

	const imageElement = (
		<button
			type="button"
			className="markdown-image-wrapper"
			onClick={handleImageClick}
			aria-label={altText ? `Zoom image: ${altText}` : "Zoom image"}
		>
			<img
				src={resolvedSrc}
				alt={altText}
				title={title}
				style={imgStyle}
				className="markdown-image"
				loading="lazy"
				onError={() => setHasError(true)}
			/>

			<div className="image-zoom-indicator" title="Zoom image">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="14"
					height="14"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2.5"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<circle cx="11" cy="11" r="8" />
					<path d="m21 21-4.3-4.3" />
					<line x1="11" x2="11" y1="8" y2="14" />
					<line x1="8" x2="14" y1="11" y2="11" />
				</svg>
			</div>
		</button>
	);

	if (captionText) {
		return (
			<figure className={`markdown-figure${alignClass}`} style={imgStyle}>
				{imageElement}
				<figcaption className="markdown-figcaption">{captionText}</figcaption>
			</figure>
		);
	}

	return (
		<div className={`markdown-image-container${alignClass}`} style={imgStyle}>
			{imageElement}
		</div>
	);
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = memo(
	({ content, className }) => {
		const [zoomImage, setZoomImage] = useState<string | null>(null);

		const handleZoom = (src: string) => {
			setZoomImage(src);
		};

		const closeZoom = useCallback(() => {
			setZoomImage(null);
		}, []);

		const handleOverlayClick = useCallback(
			(e: React.MouseEvent) => {
				if (e.target === e.currentTarget) {
					closeZoom();
				}
			},
			[closeZoom],
		);

		const handleOverlayKeyDown = useCallback(
			(e: React.KeyboardEvent) => {
				if (e.key === "Enter" || e.key === " ") {
					if (e.target === e.currentTarget) {
						e.preventDefault();
						closeZoom();
					}
				}
			},
			[closeZoom],
		);

		// Esc key listener to close modal
		useEffect(() => {
			if (!zoomImage) return;
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === "Escape") {
					closeZoom();
				}
			};
			window.addEventListener("keydown", handleKeyDown);
			return () => window.removeEventListener("keydown", handleKeyDown);
		}, [zoomImage, closeZoom]);

		return (
			<div className={className || "prose"}>
				<ReactMarkdown
					remarkPlugins={[remarkGfm, remarkMath]}
					rehypePlugins={[rehypeKatex]}
					components={{
						img({ src, alt, title }) {
							return (
								<MarkdownImage
									src={src}
									alt={alt}
									title={title}
									onZoom={handleZoom}
								/>
							);
						},
						p({ node, children, ...props }) {
							const childrenArray = node?.children || [];
							const isImageOnly =
								childrenArray.length > 0 &&
								childrenArray.every(
									(childNode: {
										type: string;
										value?: string;
										tagName?: string;
									}) => {
										if (childNode.type === "text") {
											return (childNode.value || "").trim() === "";
										}
										if (childNode.type === "element") {
											return childNode.tagName === "img";
										}
										return false;
									},
								);

							if (isImageOnly) {
								return (
									<div className="markdown-image-gallery" {...props}>
										{children}
									</div>
								);
							}

							return <p {...props}>{children}</p>;
						},
						table({ children, ...props }) {
							return (
								<div
									style={{
										overflowX: "auto",
										maxWidth: "100%",
										marginBottom: "2rem",
									}}
								>
									<table {...props} style={{ marginBottom: 0 }}>
										{children}
									</table>
								</div>
							);
						},
						code({ node, className, children, ...props }) {
							const match = /language-(\w+)/.exec(className || "");
							const isMermaid = match?.[1] === "mermaid";

							if (isMermaid) {
								return (
									<Suspense
										fallback={
											<div className="mermaid-loading">Loading diagram...</div>
										}
									>
										<Mermaid chart={String(children).replace(/\n$/, "")} />
									</Suspense>
								);
							}

							return match ? (
								<SyntaxHighlighter
									style={vscDarkPlus as { [key: string]: React.CSSProperties }}
									language={match[1]}
									PreTag="div"
									customStyle={{
										margin: 0,
										padding: "1.5rem",
										fontSize: "0.9rem",
										lineHeight: "1.5",
										backgroundColor: "transparent",
										overflowX: "visible",
										maxWidth: "100%",
									}}
								>
									{String(children).replace(/\n$/, "")}
								</SyntaxHighlighter>
							) : (
								<code className={className} {...props}>
									{children}
								</code>
							);
						},
					}}
				>
					{content}
				</ReactMarkdown>

				{zoomImage && (
					<button
						type="button"
						className="image-zoom-overlay"
						onClick={handleOverlayClick}
						onKeyDown={handleOverlayKeyDown}
						aria-label="Close zoom"
					>
						<div className="image-zoom-close">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="M18 6 6 18" />
								<path d="m6 6 12 12" />
							</svg>
						</div>
						<img src={zoomImage} className="zoom-image" alt="Enlarged view" />
					</button>
				)}
			</div>
		);
	},
);

MarkdownRenderer.displayName = "MarkdownRenderer";
